// Translation data structure for RegistrationForm component
export interface RegistrationFormTranslations {
  // Main actions and buttons
  actions: {
    submitApplication: string
    login: string
    register: string
    back: string
    next: string
    apply: string
    loginButton: string
    registerButton: string
    submitButton: string
    loginLoading: string
    registerLoading: string
    toOrganizational: string
    toLegal: string
  }

  // Form steps and navigation
  steps: {
    initial: string
    login: string
    register: string
    personal: string
    organizational: string
    legal: string
    success: string
    campRegistration: string
  }

  // Form labels and placeholders
  form: {
    email: string
    password: string
    firstName: string
    lastName: string
    phone: string
    street: string
    plz: string
    city: string
    country: string
    birthday: string
    passwordRepeat: string
    saveLogin: string
    forgotPassword: string
    selectCountry: string
    setPassword: string
    required: string
  }

  // Country options
  countries: {
    austria: string
    germany: string
    switzerland: string
    italy: string
  }

  // Section headers and descriptions
  sections: {
    welcome: {
      title: string
      subtitle: string
      description: string
      processSteps: string[]
    }
    login: {
      title: string
      subtitle: string
    }
    registration: {
      title: string
      subtitle: string
    }
    alternativeDate: {
      title: string
      description: string
      selectPlaceholder: string
      loadingAlternatives: string
      noAlternatives: string
      viewDetails: string
      selectFirst: string
    }
    equipment: {
      emergencyEquipment: string
      liftTicket: string
      multiPitchInterest: string
    }
    consent: {
      title: string
      photoConsent: string
      liabilityConsent: string
      additionalInfo: string
      liabilityConfirmation: string
    }
    newsletter: {
      title: string
      description: string
      consent: string
    }
    children: {
      title: string
      description: string
      child: string
      name: string
      birthday: string
      needsEquipment: string
      needsLiftTicket: string
      remove: string
    }
  }

  // Status messages
  status: {
    loggedIn: string
    alreadyEnrolled: {
      title: string
      message: string
    }
    success: {
      title: string
      headline: string
      message: string
    }
    loading: string
    noAccount: string
    hasAccount: string
    registerHere: string
  }

  // Error messages
  errors: {
    loginFailed: string
    submissionError: string
    fieldRequired: string
    loadingError: string
  }

  // Validation messages
  validation: {
    fieldRequired: string
    consentRequired: string
    submitFailed: string
  }
}

// German translations (current implementation)
export const germanTranslations: RegistrationFormTranslations = {
  actions: {
    submitApplication: 'Bewerbung abschicken',
    login: 'LOGIN',
    register: 'REGISTRIEREN',
    back: 'ZURÜCK',
    next: 'WEITER',
    apply: 'BEWERBEN',
    loginButton: 'Anmelden',
    registerButton: 'REGISTRIEREN',
    submitButton: 'Bewerbung abschicken',
    loginLoading: 'Anmelden...',
    registerLoading: 'Anmelden...',
    toOrganizational: 'ZUM ORGANISATORISCHEN',
    toLegal: 'RECHTLICHES',
  },

  steps: {
    initial: 'initial',
    login: 'Login',
    register: 'Registrieren',
    personal: 'Persönliche Daten',
    organizational: 'Organisatorisches',
    legal: 'Rechtliches',
    success: 'Erfolg',
    campRegistration: 'Camp Anmeldung',
  },

  form: {
    email: 'E-Mail',
    password: 'Passwort',
    firstName: 'Vorname',
    lastName: 'Nachname',
    phone: 'Telefonnummer',
    street: 'Straße, Hausnummer',
    plz: 'PLZ',
    city: 'Ort',
    country: 'Land',
    birthday: 'Geburtstag',
    passwordRepeat: 'Passwort Wiederholung',
    saveLogin: '30 Tage speichern',
    forgotPassword: 'Passwort vergessen',
    selectCountry: 'Land',
    setPassword: 'Passwort festlegen',
    required: '*',
  },

  countries: {
    austria: 'Österreich',
    germany: 'Deutschland',
    switzerland: 'Schweiz',
    italy: 'Italien',
  },

  sections: {
    welcome: {
      title: 'Schön, dass du dabei sein willst',
      subtitle: 'Deine Bewerbung für',
      description: 'Hier findest du alles, was du für die Anmeldung benötigst.',
      processSteps: ['Bewerbung', 'Prüfung der Bewerbung', 'Zu- / Absage per mail'],
    },
    login: {
      title: 'Login',
      subtitle: 'Login',
    },
    registration: {
      title: 'Registrieren',
      subtitle: 'Registrieren',
    },
    alternativeDate: {
      title: 'Ersatztermin',
      description:
        'Falls der gewünschte Termin ausgebucht ist, kannst du hier einen alternativen Termin auswählen.',
      selectPlaceholder: 'Kein alternatives Camp',
      loadingAlternatives: 'Lade Alternativen...',
      noAlternatives: 'Keine Alternativen verfügbar',
      viewDetails: 'Genaue Infos zum ausgewählten Camp ↗',
      selectFirst: 'Wähle zuerst ein alternatives Camp',
    },
    equipment: {
      emergencyEquipment:
        'Ich benötige für dieses Camp eine Leihausrüstung. Der Verleih ist über uns kostenlos.',
      liftTicket: 'Ich benötige für dieses Camp ein Ticket',
      multiPitchInterest: 'Ich habe Interesse an Mehrseillängen',
    },
    consent: {
      title: 'Bestätigung Foto & Videoaufnahmen und Haftungserklärung',
      photoConsent:
        'Ich erkläre mich damit einverstanden, dass während des Camps <a href="/foto-und-videoaufnahmen" target="_blank" rel="noopener noreferrer" class="underline">Foto- und Videoaufnahmen</a> gemacht werden.',
      liabilityConsent:
        'Hiermit bestätige ich, die <a href="/haftungserklaerung-teilnahmebedingungen" target="_blank" rel="noopener noreferrer" class="underline">Haftungserklärung</a> gelesen zu haben und zu akzeptieren.',
      additionalInfo:
        'Ich habe das oben Genannte vollständig gelesen, verstanden und bin damit einverstanden.',
      liabilityConfirmation:
        'Mit dem Klick auf die Bestätigung über die Haftungserklärung bestätige ich, dass ich diese vollständig gelesen sowie verstanden habe und damit einverstanden bin.',
    },
    newsletter: {
      title: 'Zur Newsletter Anmeldung',
      description:
        'Wir senden dir regelmäßig Updates zu den aktuellen Themen und exklusive Inhalte. Du erhältst eine separate E-Mail, um deine Newsletter-Anmeldung zu bestätigen.',
      consent: 'ich möchte über aktuelle Themen informiert werden und exklusive Inhalte erhalten.',
    },
    children: {
      title: 'Kinder',
      description: 'Fügen Sie hier die Daten Ihrer Kinder hinzu.',
      child: 'Kind',
      name: 'Name',
      birthday: 'Geburtstag',
      needsEquipment: 'Leihausrüstung benötigt',
      needsLiftTicket: 'Ticket benötigt',
      remove: 'Entfernen',
    },
  },

  status: {
    loggedIn: 'Eingeloggt als:',
    alreadyEnrolled: {
      title: 'Already Enrolled',
      message:
        'Hallo {firstName}! Du hast dich bereits erfolgreich für das Camp "{campTitle}" beworben. Du musst nichts weiter tun. Wir werden dich per E-Mail über den Status deiner Bewerbung informieren, sobald wir deinen Camp-Platz bestätigen können. Bitte überprüfe auch dein Spam-Postfach und füge ggf. mail@saac.at als Ausnahme hinzu.',
    },
    success: {
      title: 'Success',
      headline: 'Deine Anmeldung ist bei uns eingegangen!',
      message:
        'Danke für deine Bewerbung! Du bekommst eine Bestätigungsmail von uns - bitte check auch dein Spam-Postfach und füge ggf. anmeldung@saac.at als Ausnahme hinzu. Bitte beachte, dass das noch keine fixe Zusage ist. Du musst jetzt nichts mehr weiter tun, als Geduld haben. Wir prüfen die Daten und melden uns bei dir, sobald wir deinen Campplatz bestätigen können.',
    },
    loading: 'Lade Termine...',
    noAccount: 'Noch keinen Account?',
    hasAccount: 'Du hast bereits einen Account?',
    registerHere: 'Hier registrieren',
  },

  errors: {
    loginFailed: 'Login fehlgeschlagen',
    submissionError: 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.',
    fieldRequired: 'Dieses Feld ist erforderlich',
    loadingError: 'Fehler beim Laden der Termine',
  },

  validation: {
    fieldRequired: 'Dieses Feld ist erforderlich',
    consentRequired: 'Bitte stimme den Einverständniserklärungen zu.',
    submitFailed: 'Die Anmeldung konnte nicht abgeschickt werden. Bitte versuche es erneut.',
  },
}

// English translations (for future implementation when isEnglishCamp is true)
export const englishTranslations: RegistrationFormTranslations = {
  actions: {
    submitApplication: 'Submit Application',
    login: 'LOGIN',
    register: 'REGISTER',
    back: 'BACK',
    next: 'NEXT',
    apply: 'APPLY',
    loginButton: 'Login',
    registerButton: 'REGISTER',
    submitButton: 'Submit Application',
    loginLoading: 'Logging in...',
    registerLoading: 'Logging in...',
    toOrganizational: 'TO ORGANIZATIONAL',
    toLegal: 'LEGAL',
  },

  steps: {
    initial: 'initial',
    login: 'Login',
    register: 'Register',
    personal: 'Personal Information',
    organizational: 'Organizational',
    legal: 'Legal',
    success: 'Success',
    campRegistration: 'Camp Registration',
  },

  form: {
    email: 'Email',
    password: 'Password',
    firstName: 'First Name',
    lastName: 'Last Name',
    phone: 'Phone Number',
    street: 'Street, House Number',
    plz: 'ZIP Code',
    city: 'City',
    country: 'Country',
    birthday: 'Birthday',
    passwordRepeat: 'Repeat Password',
    saveLogin: 'Save for 30 days',
    forgotPassword: 'Forgot Password',
    selectCountry: 'Country',
    setPassword: 'Set Password',
    required: '*',
  },

  countries: {
    austria: 'Austria',
    germany: 'Germany',
    switzerland: 'Switzerland',
    italy: 'Italy',
  },

  sections: {
    welcome: {
      title: 'Great that you want to be part of it',
      subtitle: 'Your application for',
      description: 'Here you will find everything you need for registration.',
      processSteps: ['Application', 'Review of application', 'Acceptance / Rejection by email'],
    },
    login: {
      title: 'Login',
      subtitle: 'Login',
    },
    registration: {
      title: 'Register',
      subtitle: 'Register',
    },
    alternativeDate: {
      title: 'Alternative Date',
      description: 'If the desired date is fully booked, you can select an alternative date here.',
      selectPlaceholder: 'No alternative camp',
      loadingAlternatives: 'Loading alternatives...',
      noAlternatives: 'No alternatives available',
      viewDetails: 'Detailed info about selected camp ↗',
      selectFirst: 'Please select an alternative camp first',
    },
    equipment: {
      emergencyEquipment:
        'I need rental equipment (avalanche transceiver, probe, shovel) for this camp. The rental is free through us.',
      liftTicket: 'I need a ticket for this camp',
      multiPitchInterest: 'I am interested in multi-pitch climbing',
    },
    consent: {
      title: 'Confirmation of Photo & Video Recordings and Liability Declaration',
      photoConsent: 'I agree that photos and videos will be taken during the camp.',
      liabilityConsent:
        'I hereby confirm that I have read and accept the <a href="/haftungsausschluss" target="_blank" rel="noopener noreferrer" class="underline">liability declaration</a>.',
      additionalInfo: 'I have read, understood and agree to the above information completely.',
      liabilityConfirmation:
        'By clicking the confirmation about the liability waiver, I confirm that I have read, understood and agree to it completely.',
    },
    newsletter: {
      title: 'Newsletter Subscription',
      description:
        'We send you regular updates on current topics and exclusive content. You will receive a separate email to confirm your newsletter subscription.',
      consent: 'I want to be informed about current topics and receive exclusive content.',
    },
    children: {
      title: 'Children',
      description: "Add your children's information here.",
      child: 'Child',
      name: 'Name',
      birthday: 'Birthday',
      needsEquipment: 'Rental equipment needed',
      needsLiftTicket: 'Ticket needed',
      remove: 'Remove',
    },
  },

  status: {
    loggedIn: 'Logged in as:',
    alreadyEnrolled: {
      title: 'Already Enrolled',
      message:
        'Hello {firstName}! You have already successfully applied for the camp "{campTitle}". You don\'t need to do anything else. We will inform you by email about the status of your application as soon as we can confirm your camp spot. Please also check your spam folder and add mail@saac.at as an exception if necessary.',
    },
    success: {
      title: 'Success',
      headline: 'Your registration has been received!',
      message:
        "Thank you for your application! You will receive a confirmation email from us - please also check your spam folder and add anmeldung@saac.at as an exception if necessary. Please note that this is not yet a final confirmation. You don't need to do anything else now, just be patient. We will review the data and contact you as soon as we can confirm your camp spot.",
    },
    loading: 'Loading dates...',
    noAccount: "Don't have an account yet?",
    hasAccount: 'Already have an account?',
    registerHere: 'Register here',
  },

  errors: {
    loginFailed: 'Login failed',
    submissionError: 'An error occurred. Please try again.',
    fieldRequired: 'This field is required',
    loadingError: 'Error loading dates',
  },

  validation: {
    fieldRequired: 'This field is required',
    consentRequired: 'Please accept the declarations of consent.',
    submitFailed: 'Your registration could not be sent. Please try again.',
  },
}

// Function to get translations based on language
export const getRegistrationFormTranslations = (
  isEnglish: boolean,
): RegistrationFormTranslations => {
  return isEnglish ? englishTranslations : germanTranslations
}
